/**
 * LakehouseStorageList Component
 *
 * Displays a list of storage accounts mapped to a lakehouse with management actions.
 * Part of Phase 8: Lakehouse Storage Mapping.
 */

import React, { useState } from 'react';
import {
  useLakehouseStorage,
  useRemoveStorageAccount,
  useSetAsPrimary,
  useChangePurpose,
  useDeactivateMapping,
  useReactivateMapping,
  LakehouseStorageMapping,
  StorageAccountPurpose,
} from '../../hooks/useLakehouseStorage';
import { formatEasternDate } from '@/utils/dateFormatter';

interface LakehouseStorageListProps {
  lakehouseId: string;
  onAddNew: () => void;
}

// Purpose display labels
const purposeLabels: Record<StorageAccountPurpose, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  archive: 'Archive',
  overflow: 'Overflow',
};

// Purpose colors
const purposeColors: Record<StorageAccountPurpose, string> = {
  primary: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  secondary: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  archive: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  overflow: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
};

// Status colors
const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  pending_removal: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

// Format bytes to human-readable
function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined || bytes === null) return '0 B';
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

// Format date relative to now
function formatRelativeDate(date: string | null | undefined): string {
  if (!date) return 'Never';
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 1) return 'Less than an hour ago';
  if (diffHours < 24) return `${diffHours} hours ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatEasternDate(d);
}

export function LakehouseStorageList({ lakehouseId, onAddNew }: LakehouseStorageListProps) {
  const { data: mappings, isLoading, error, refetch } = useLakehouseStorage(lakehouseId);
  const removeStorage = useRemoveStorageAccount(lakehouseId);
  const setAsPrimary = useSetAsPrimary(lakehouseId);
  const changePurpose = useChangePurpose(lakehouseId);
  const deactivateMapping = useDeactivateMapping(lakehouseId);
  const reactivateMapping = useReactivateMapping(lakehouseId);

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [forceDelete, setForceDelete] = useState(false);
  const [purposeDropdown, setPurposeDropdown] = useState<string | null>(null);

  const handleRemove = async (mappingId: string) => {
    try {
      await removeStorage.mutateAsync({ mappingId, force: forceDelete });
      setDeleteConfirm(null);
      setForceDelete(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleSetPrimary = async (mappingId: string) => {
    try {
      await setAsPrimary.mutateAsync(mappingId);
    } catch {
      // Error handled by mutation
    }
  };

  const handleChangePurpose = async (mappingId: string, purpose: StorageAccountPurpose) => {
    try {
      await changePurpose.mutateAsync({ mappingId, purpose });
      setPurposeDropdown(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleToggleStatus = async (mapping: LakehouseStorageMapping) => {
    try {
      if (mapping.status === 'active') {
        await deactivateMapping.mutateAsync(mapping.id);
      } else {
        await reactivateMapping.mutateAsync(mapping.id);
      }
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <div className='bg-[var(--color-bg-card)] shadow rounded-lg p-6'>
        <div className='animate-pulse space-y-4'>
          <div className='h-8 bg-[var(--color-bg-subtle)] rounded w-1/4'></div>
          <div className='h-4 bg-[var(--color-bg-subtle)] rounded w-full'></div>
          <div className='h-4 bg-[var(--color-bg-subtle)] rounded w-full'></div>
          <div className='h-4 bg-[var(--color-bg-subtle)] rounded w-full'></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='bg-[var(--color-bg-card)] shadow rounded-lg p-6'>
        <div className='text-red-600 dark:text-red-400'>
          <h3 className='text-lg font-medium'>Error loading storage accounts</h3>
          <p className='mt-1 text-sm'>{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className='mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700'
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-[var(--color-bg-card)] shadow rounded-lg'>
      {/* Header */}
      <div className='px-4 py-5 sm:px-6 border-b border-[var(--color-border)]'>
        <div className='flex items-center justify-between'>
          <div>
            <h3 className='text-lg font-medium leading-6 text-[var(--color-text-primary)]'>
              Storage Accounts
            </h3>
            <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
              Manage storage accounts mapped to this lakehouse
            </p>
          </div>
          <button
            onClick={onAddNew}
            className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          >
            <svg
              className='-ml-1 mr-2 h-5 w-5'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 4v16m8-8H4'
              />
            </svg>
            Add Storage Account
          </button>
        </div>
      </div>

      {/* List */}
      <div className='divide-y divide-[var(--color-border)]'>
        {!mappings || mappings.length === 0 ? (
          <div className='p-6 text-center'>
            <svg
              className='mx-auto h-12 w-12 text-[var(--color-text-secondary)]'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4'
              />
            </svg>
            <h3 className='mt-2 text-sm font-medium text-[var(--color-text-primary)]'>
              No storage accounts
            </h3>
            <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
              Add a storage account to start backing up this lakehouse.
            </p>
            <div className='mt-6'>
              <button
                onClick={onAddNew}
                className='inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700'
              >
                <svg
                  className='-ml-1 mr-2 h-5 w-5'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 4v16m8-8H4'
                  />
                </svg>
                Add Storage Account
              </button>
            </div>
          </div>
        ) : (
          mappings.map((mapping) => (
            <div key={mapping.id} className='p-4 sm:px-6 hover:bg-[var(--color-bg-subtle)]'>
              <div className='flex items-center justify-between'>
                {/* Left side - Name and badges */}
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <h4 className='text-sm font-medium text-[var(--color-text-primary)] truncate'>
                      {mapping.storageAccountName || 'Unknown Storage Account'}
                    </h4>
                    {mapping.isPrimary && (
                      <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'>
                        ★ Primary
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${purposeColors[mapping.purpose] || purposeColors['secondary']}`}
                    >
                      {purposeLabels[mapping.purpose] || 'Secondary'}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[mapping.status] || statusColors['active']}`}
                    >
                      {(mapping.status || 'active').replace('_', ' ')}
                    </span>
                  </div>
                  <div className='mt-1 flex items-center gap-4 text-sm text-[var(--color-text-secondary)]'>
                    {mapping.containerName && <span>Container: {mapping.containerName}</span>}
                    {mapping.region && <span>Region: {mapping.region}</span>}
                  </div>
                  <div className='mt-1 flex items-center gap-4 text-xs text-[var(--color-text-secondary)]'>
                    <span>Backups: {mapping.backupCount ?? 0}</span>
                    <span>Storage: {formatBytes(mapping.totalStorageBytes)}</span>
                    <span>Last Backup: {formatRelativeDate(mapping.lastBackupAt)}</span>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className='flex items-center gap-2 ml-4'>
                  {/* Set as primary button (if not already primary) */}
                  {!mapping.isPrimary && mapping.status === 'active' && (
                    <button
                      onClick={() => handleSetPrimary(mapping.id)}
                      disabled={setAsPrimary.isPending}
                      className='text-sm text-[var(--color-accent)] hover:text-blue-500 dark:hover:text-blue-300'
                      title='Set as primary'
                    >
                      Set Primary
                    </button>
                  )}

                  {/* Purpose dropdown */}
                  <div className='relative'>
                    <button
                      onClick={() =>
                        setPurposeDropdown(purposeDropdown === mapping.id ? null : mapping.id)
                      }
                      className='text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                      title='Change purpose'
                    >
                      Purpose ▾
                    </button>
                    {purposeDropdown === mapping.id && (
                      <div className='absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-[var(--color-bg-card)] ring-1 ring-black ring-opacity-5 z-10'>
                        <div className='py-1'>
                          {(Object.keys(purposeLabels) as StorageAccountPurpose[]).map(
                            (purpose) => (
                              <button
                                key={purpose}
                                onClick={() => handleChangePurpose(mapping.id, purpose)}
                                className={`block w-full text-left px-4 py-2 text-sm ${
                                  mapping.purpose === purpose
                                    ? 'bg-[var(--color-bg-subtle)] text-[var(--color-text-primary)]'
                                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
                                }`}
                              >
                                {purposeLabels[purpose]}
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Toggle active/inactive */}
                  <button
                    onClick={() => handleToggleStatus(mapping)}
                    disabled={deactivateMapping.isPending || reactivateMapping.isPending}
                    className={`text-sm ${
                      mapping.status === 'active'
                        ? 'text-yellow-600 hover:text-yellow-500 dark:text-yellow-400'
                        : 'text-green-600 hover:text-green-500 dark:text-green-400'
                    }`}
                  >
                    {mapping.status === 'active' ? 'Deactivate' : 'Activate'}
                  </button>

                  {/* Remove button */}
                  {deleteConfirm === mapping.id ? (
                    <div className='flex items-center gap-2'>
                      <label className='flex items-center text-xs text-[var(--color-text-secondary)]'>
                        <input
                          type='checkbox'
                          checked={forceDelete}
                          onChange={(e) => setForceDelete(e.target.checked)}
                          className='mr-1'
                        />
                        Force
                      </label>
                      <button
                        onClick={() => handleRemove(mapping.id)}
                        disabled={removeStorage.isPending}
                        className='text-sm text-red-600 hover:text-red-500'
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => {
                          setDeleteConfirm(null);
                          setForceDelete(false);
                        }}
                        className='text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(mapping.id)}
                      className='text-sm text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300'
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              {/* Error message for this mapping */}
              {removeStorage.isError && deleteConfirm === mapping.id && (
                <div className='mt-2 text-sm text-red-600 dark:text-red-400'>
                  {(removeStorage.error as Error).message}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default LakehouseStorageList;
